<script setup>
import { nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import PlaygroundSetup from './LilithPlaygroundSetup.vue';
import { sendLilithPlaygroundMessage } from 'dashboard/api/lilithTeam';

const props = defineProps({
  assistant: { type: Object, required: true },
  canManage: { type: Boolean, default: false },
  assistantId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['assistantUpdated']);

const { accountId } = useAccount();
const { t } = useI18n();

const messages = ref([]);
const draft = ref('');
const configuration = ref(null);
const loading = ref(false);
const error = ref('');
const transcript = ref(null);
let revision = 0;

const resetConversation = () => {
  revision += 1;
  messages.value = [];
  draft.value = '';
  error.value = '';
};

watch(
  () => props.assistantId,
  () => resetConversation()
);

const scrollToBottom = async () => {
  await nextTick();
  if (transcript.value) {
    transcript.value.scrollTop = transcript.value.scrollHeight;
  }
};

const historyForApi = () =>
  messages.value.map(message => ({
    role: message.role,
    content: message.content,
    ...(message.agentName ? { agent_name: message.agentName } : {}),
  }));

const sendMessage = async () => {
  const current = revision;
  const content = draft.value.trim();
  if (!content || loading.value) return;

  messages.value.push({ role: 'user', content });
  draft.value = '';
  error.value = '';
  await scrollToBottom();

  try {
    loading.value = true;
    const response = await sendLilithPlaygroundMessage(
      accountId.value,
      props.assistantId,
      content,
      historyForApi(),
      configuration.value
    );
    if (current !== revision) return;
    const assistantText = String(response.data?.response || '').trim();
    if (!assistantText) {
      throw new Error(t('TEAM_LILITH.PLAYGROUND.EMPTY_RESPONSE'));
    }
    messages.value.push({
      role: 'assistant',
      content: assistantText,
      agentName: response.data?.agent_name || '',
      agentLabel: response.data?.agent_label || '',
      parts: response.data?.response_parts || [],
      effects: response.data?.simulated_effects || [],
      runDetails: response.data?.run_details || [],
    });
    await scrollToBottom();
  } catch (requestError) {
    if (current !== revision) return;
    messages.value.pop();
    draft.value = content;
    error.value =
      requestError?.response?.data?.error ||
      requestError?.response?.data?.detail ||
      requestError?.message ||
      t('TEAM_LILITH.PLAYGROUND.ERROR');
  } finally {
    loading.value = false;
  }
};

const handleKeydown = event => {
  if (event.isComposing || event.shiftKey) return;
  event.preventDefault();
  sendMessage();
};
</script>

<template>
  <div class="flex flex-1 overflow-hidden bg-n-surface-1">
    <div
      class="mx-auto flex w-full max-w-5xl flex-col gap-4 px-6 py-8 lg:px-10"
    >
      <header class="flex items-start justify-between gap-4">
        <div>
          <div
            class="text-xs font-semibold uppercase tracking-[0.12em] text-n-iris-10"
          >
            {{ $t('TEAM_LILITH.PLAYGROUND.EYEBROW') }}
          </div>
          <h1 class="mt-1 text-2xl font-semibold text-n-slate-12">
            {{ $t('TEAM_LILITH.PLAYGROUND.TITLE') }}
          </h1>
          <p class="mt-2 max-w-3xl text-sm leading-6 text-n-slate-11">
            {{ $t('TEAM_LILITH.PLAYGROUND.DESCRIPTION') }}
          </p>
        </div>
        <Button
          :label="$t('TEAM_LILITH.PLAYGROUND.RESET')"
          icon="i-lucide-rotate-ccw"
          variant="ghost"
          color="slate"
          @click="resetConversation"
        />
      </header>

      <PlaygroundSetup
        :key="assistantId"
        :assistant="assistant"
        :can-manage="canManage"
        :disabled="loading"
        @configuration="configuration = $event"
        @assistant-updated="emit('assistantUpdated')"
      />
      <section
        ref="transcript"
        class="min-h-0 flex-1 overflow-y-auto rounded-xl border border-n-weak bg-n-surface-2 p-4"
      >
        <div
          v-if="messages.length === 0"
          class="flex min-h-56 items-center justify-center text-center text-sm text-n-slate-10"
        >
          {{ $t('TEAM_LILITH.PLAYGROUND.EMPTY') }}
        </div>

        <div v-else class="flex flex-col gap-3">
          <article
            v-for="(message, index) in messages"
            :key="index"
            class="rounded-lg border border-n-weak px-4 py-3"
            :class="
              message.role === 'user'
                ? 'ml-auto max-w-[85%] bg-n-alpha-2'
                : 'mr-auto max-w-[85%] bg-n-background'
            "
          >
            <div class="mb-1 text-xs font-semibold text-n-slate-10">
              {{
                message.role === 'user'
                  ? $t('TEAM_LILITH.PLAYGROUND.YOU')
                  : $t('TEAM_LILITH.PLAYGROUND.ASSISTANT')
              }}
            </div>
            <p class="whitespace-pre-wrap text-sm leading-6 text-n-slate-12">
              {{ message.content }}
            </p>
            <p v-if="message.agentName" class="mt-2 text-xs text-n-slate-10">
              {{ $t('TEAM_LILITH.PLAYGROUND.AGENT') }}: {{ message.agentLabel }}
            </p>
            <details
              v-if="message.parts?.length"
              class="mt-2 text-xs text-n-slate-11"
            >
              <summary>{{ $t('TEAM_LILITH.PLAYGROUND.PARTS') }}</summary>
              <pre class="overflow-x-auto whitespace-pre-wrap">{{
                message.parts.map(part => part.text).join('\n\n')
              }}</pre>
            </details>
            <details
              v-if="message.runDetails?.length"
              class="mt-2 text-xs text-n-slate-11"
            >
              <summary>{{ $t('TEAM_LILITH.PLAYGROUND.RUN_DETAILS') }}</summary>
              <ol class="mt-2 space-y-2">
                <li
                  v-for="(entry, entryIndex) in message.runDetails"
                  :key="entryIndex"
                >
                  <span
                    >{{ entry.event }} · {{ entry.agent || entry.tool }}</span
                  >
                  <pre
                    v-if="entry.preview"
                    class="whitespace-pre-wrap break-all"
                    >{{ entry.preview }}</pre
                  >
                </li>
              </ol>
            </details>
            <p
              v-for="(effect, effectIndex) in message.effects || []"
              :key="effectIndex"
              class="mt-2 text-xs text-n-amber-11"
            >
              {{ $t('TEAM_LILITH.PLAYGROUND.SIMULATED') }}: {{ effect.effect }}
            </p>
          </article>

          <article
            v-if="loading"
            class="mr-auto max-w-[85%] rounded-lg border border-n-weak bg-n-background px-4 py-3 text-sm text-n-slate-10"
          >
            {{ $t('TEAM_LILITH.PLAYGROUND.THINKING') }}
          </article>
        </div>
      </section>

      <p
        v-if="error"
        class="rounded-lg border border-n-ruby-6 bg-n-ruby-2 px-3 py-2 text-sm text-n-ruby-11"
      >
        {{ error }}
      </p>

      <div class="rounded-xl border border-n-weak bg-n-surface-2 p-3">
        <textarea
          v-model="draft"
          rows="3"
          class="w-full resize-none border-0 bg-transparent text-sm text-n-slate-12 outline-none placeholder:text-n-slate-9"
          :placeholder="$t('TEAM_LILITH.PLAYGROUND.PLACEHOLDER')"
          :disabled="loading"
          @keydown.enter.exact="handleKeydown"
        />
        <div class="mt-2 flex items-center justify-between gap-3">
          <span class="text-xs leading-5 text-n-slate-10">
            {{ $t('TEAM_LILITH.PLAYGROUND.LIVE_NOTE') }}
          </span>
          <Button
            :label="$t('TEAM_LILITH.PLAYGROUND.SEND')"
            icon="i-lucide-send"
            :disabled="loading || !draft.trim()"
            @click="sendMessage"
          />
        </div>
      </div>
    </div>
  </div>
</template>
